import styled from '@emotion/styled';

import { type IGLIcon } from '@group-link-one/gl-icons';
import {
  BaseProperties,
  ColorVariables,
  SPBox,
  SPButton,
  SPTypography,
} from '@group-link-one/gl-ui/sonarprint';
import type { ErrorInfo, ReactNode } from 'react';
import { env } from '@/env';

interface ErrorLayoutProps {
  type: 'error' | 'not-found';
  image?: string;
  title: string;
  description: string;
  primaryButton?: {
    text: string;
    onClick: () => void;
    icon?: IGLIcon['variant'];
  };
  secondaryButton?: {
    text: string;
    onClick: () => void;
    icon?: IGLIcon['variant'];
  };
  error?: Error;
  errorInfo?: ErrorInfo;
  hasButtons?: boolean;
}

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  border: 1px solid ${ColorVariables.BASE.BASE_COLOR_QUINARY_STROKE};

  background-color: ${ColorVariables.COLOR.COLOR_QUINARY_BACKGROUND};
  height: 100%;
  border-radius: ${BaseProperties.RADIUS.LG};
  gap: ${BaseProperties.SPACING.M};
  flex: 1;

  .error-layout-buttons {
    max-width: 600px;
    margin-top: 22px;
  }

  .error-layout-logo {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    width: 100%;
    margin-bottom: 30px;

    max-width: 1280px;

    img {
      max-width: 240px;
    }
  }

  .error-layout-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
    max-width: 1280px;
  }

  > img {
    height: 250px;
  }

  &.error-layout-not-found {
    min-height: 100vh;

    button {
      margin: 0 auto;
      max-width: 300px;
    }
  }
`;

/**
 * ErrorLayout Component
 * Reusable layout for error and not-found pages
 *
 * @param type - 'error' shows error details in dev mode and two buttons, 'not-found' shows only one action button
 * @param image - Optional image URL to display
 * @param title - Main title text
 * @param description - Description text
 * @param primaryButton - Primary action button configuration
 * @param secondaryButton - Secondary action button configuration (only for 'error' type)
 * @param error - Error object (only for 'error' type in dev mode)
 * @param errorInfo - Error info object (only for 'error' type in dev mode)
 */
export function ErrorLayout({
  type,
  image,
  title,
  description,
  primaryButton,
  secondaryButton,
  error,
  errorInfo,
  hasButtons = true,
}: ErrorLayoutProps): ReactNode {
  return (
    <ErrorContainer className={`error-layout error-layout-${type}`}>
      {/* {type === 'not-found' && (
        <div className='error-layout-logo'>
          <img src={currentLogoImage} alt='Not found illustration' />
        </div>
      )} */}
      {image && (
        <img
          loading='lazy'
          src={image}
          alt={type === 'error' ? 'Error illustration' : 'Not found illustration'}
        />
      )}

      <div className='error-layout-container'>
        <SPTypography
          text={title}
          fontSize={BaseProperties.FONT_SIZE.SUBTITLE_2}
          fontWeight={600}
          color={ColorVariables.ACCENT.ACCENT_COLOR_PRIMARY_TEXT}
          textAlign='center'
        />

        <SPTypography
          text={description}
          fontSize={BaseProperties.FONT_SIZE.BODY_1}
          color={ColorVariables.BASE.BASE_COLOR_PRIMARY_TEXT}
          textAlign='center'
        />

        {type === 'error' && env.VITE_NODE_ENV !== 'production' && error && (
          <details style={{ marginTop: '1rem', textAlign: 'left' }}>
            <summary>Detalhes do erro (desenvolvimento)</summary>
            <pre style={{ fontSize: '12px', overflow: 'auto' }}>
              {error.toString()}
              {errorInfo?.componentStack}
            </pre>
          </details>
        )}

        {hasButtons && (
          <SPBox gap={BaseProperties.SPACING.L} className='error-layout-buttons'>
            <SPButton
              text={primaryButton?.text}
              variant='primary'
              onClick={primaryButton?.onClick}
              icon={primaryButton?.icon}
            />

            {type === 'error' && secondaryButton && (
              <SPButton
                text={secondaryButton.text}
                variant='secondary'
                onClick={secondaryButton.onClick}
                icon={secondaryButton.icon}
              />
            )}
          </SPBox>
        )}
      </div>
    </ErrorContainer>
  );
}
